import { Autocomplete, Checkbox, Divider, FormControlLabel, Grid, TextField, Typography, Tooltip, Box } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import * as Constant from 'common/Constant';
import moment from 'moment-timezone';
import React, { useEffect, useState, useCallback } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { generateRandomString } from 'utils/common';
import { idsEqual, normalizeId } from 'utils/id';
import * as ValidationUtil from 'utils/ValidationUtil';
import { renderTruncatedTags, DROPDOWN_OPTION_MAX_WIDTH } from 'utils/chipUtil';
import SpecialExpenseForm from './specialExpenseForm';

const PlanExpenseDetail = (props) => {
  const { duplicateError, resetDuplicateError } = props;
  const { control, watch, setValue, getValues, trigger } = useFormContext();
  const { fields } = useFieldArray({
    control,
    name: 'planExpenseRates',
    shouldUnregister: true
  });

  const [plansForYears, setPlansForYears] = useState([]);
  const [planYearOptions, setPlanYearOptions] = useState([]);

  const watchPlanEndDate = watch('endDate', null);
  const watchPlanStartDate = watch('startDate', null);
  const watchRateByTier = watch('isRateByTier', false);
  const watchRateBasis = watch('rateBasis', '');
  const watchPlanYear = watch('planYears', []);
  const watchExpenseName = watch('expenseName', '');
  const watchRate = watch('rate', null);
  const totalPlanYears = useSelector((state) => state.MD.mdCommon.allPlanYearsForTenant);
  const plansNames = useSelector((state) => state.MD.mdCommon.allPlanNames);
  const expenseCategories = useSelector((state) => state.MD.planExpense.expenseCategory);
  const expenseNames = useSelector((state) => state.MD.planExpense.expenseNames);
  const rateBasis = useSelector((state) => state.MD.planExpense.rateBasis);

  const allElectionTiers = useSelector((state) => state.MD.mdCommon.allElectionTiers);
  // filter plans first (used by handlePlanYearChange below)
  const filterPlans = useCallback(
    (years) => {
      let filteredList = [];
      const ahpmSet = new Set(years.map((p) => normalizeId(p.id)));
      if (plansNames && Array.isArray(plansNames) && ahpmSet.size > 0) {
        filteredList = plansNames.filter((plan) =>
          ahpmSet.has(normalizeId(plan.ahpmId))
        );
        years.forEach((year) => {
          filteredList
            .filter((plan) => idsEqual(year.id, plan.ahpmId))
            .forEach((p) => {
              p.year = year.name;
            });
        });
        filteredList.unshift({ id: generateRandomString(), planName: Constant.ALL });
      }
      const sorted = filteredList.sort((a, b) => a.year - b.year);
      setPlansForYears(sorted);
      const currentSelected = getValues('planName');
      if (Array.isArray(currentSelected) && currentSelected.length) {
        const filteredSelectedPlans = currentSelected.filter((plan) =>
          ahpmSet.has(normalizeId(plan.ahpmId))
        );
        // only update if length or ids differ
        if (filteredSelectedPlans.length !== currentSelected.length || filteredSelectedPlans.some((p, i) => p.id !== currentSelected[i].id)) {
          setValue('planName', filteredSelectedPlans);
        }
      }
    },
    [plansNames, getValues, setValue]
  );

  const handlePlanYearChange = useCallback(
    (years) => {
      filterPlans(years);
      if (years.length === 1) {
        const planInfo = totalPlanYears.find((py) =>
          idsEqual(py.id, years[0].id)
        );
        setValue('startDate', planInfo?.startDate || null);
        setValue('endDate', planInfo?.endDate || null);
      } else {
        setValue('startDate', null);
        setValue('endDate', null);
      }
    },
    [filterPlans, setValue, totalPlanYears]
  );

  const createExpenseRates = useCallback(
    (rate) => {
      const electiontiers = allElectionTiers.map((et, i) => ({
        divisionId: '',
        tierId: et.id,
        name: et.electionTier,
        rate: rate ? rate : getValues(`planExpenseRates[${i}].rate`),
        startDate: null,
        endDate: null
      }));
      setValue('planExpenseRates', electiontiers);
    },
    [allElectionTiers, getValues, setValue]
  );

  useEffect(() => {
    if (watchRateByTier && Array.isArray(allElectionTiers)) {
      createExpenseRates(null);
    }
  }, [watchRateByTier, allElectionTiers, createExpenseRates]);

  useEffect(() => {
    if (Array.isArray(allElectionTiers)) {
      createExpenseRates(watchRate);
    }
  }, [watchRate, allElectionTiers, createExpenseRates]);

  const setPlanYearSelectOptions = useCallback(() => {
    const d = totalPlanYears.filter((a) => a.planYearName).map((a) => ({ id: a.id, name: a.planYearName, label: a.planYearName }));
    d.unshift({ id: generateRandomString(), name: Constant.ALL, label: Constant.ALL });
    return d;
  }, [totalPlanYears]);

  useEffect(() => {
    setPlanYearOptions(setPlanYearSelectOptions());
  }, [totalPlanYears, setPlanYearSelectOptions]);

  useEffect(() => {
    if (watchPlanYear) {
      handlePlanYearChange(watchPlanYear);
    }
  }, [watchPlanYear, handlePlanYearChange]);

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={4}>
          <Grid container spacing={2} direction="column">
            <Grid item>
              <Controller
                rules={{
                  required: Constant.ERROR_MSG_REQUIRED_FIELD
                }}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    {...field}
                    id="input-planyear"
                    multiple
                    options={planYearOptions}
                    value={getValues('planYears') || []}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderOption={(props, option) => {
                      // Remove key from props and pass it directly
                      const { key, ...rest } = props;
                      return (
                        <li key={option.id} {...rest}>
                          {option.name}
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Plan Year" error={error || duplicateError ? true : false} helperText={error?.message || duplicateError || null} />
                    )}
                    onChange={(e, data) => {
                      resetDuplicateError();

                      if (data.find((op) => op.label === Constant.ALL)) {
                        e.preventDefault();
                        setValue(
                          'planYears',
                          planYearOptions.filter((option) => option.label !== Constant.ALL)
                        );
                      } else {
                        field.onChange(data);
                      }
                    }}
                  />
                )}
                defaultValue={[]}
                name="planYears"
                control={control}
              />
            </Grid>
            <Grid item lg>
              <Controller
                name="planName"
                rules={{
                  required: Constant.ERROR_MSG_REQUIRED_FIELD
                }}
                control={control}
                defaultValue={[]}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    {...field}
                    id="input-plan"
                    multiple
                    options={plansForYears}
                    value={getValues('planName') || []}
                    getOptionLabel={(option) => `${option.planName} (${option.year})`}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderOption={(props, option) => {
                      const label = `${option.planName} (${option.year})`;
                      const { key, ...rest } = props;
                      return (
                        <li key={option.id} {...rest}>
                          <Tooltip title={label} placement="right" arrow>
                            <Box
                              sx={{
                                display: 'block',
                                maxWidth: DROPDOWN_OPTION_MAX_WIDTH,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                              {label}
                            </Box>
                          </Tooltip>
                        </li>
                      );
                    }}
                    renderTags={renderTruncatedTags((opt) => `${opt.planName} (${opt.year})`)}
                    renderInput={(params) => <TextField {...params} label="Plan Name" error={!!error} helperText={error?.message ?? null} />}
                    onChange={(e, data) => {
                      resetDuplicateError();
                      if (data.find((pn) => pn.planName === Constant.ALL)) {
                        e.preventDefault();
                        setValue(
                          'planName',
                          plansForYears.filter((option) => option.planName !== Constant.ALL)
                        );
                      } else {
                        field.onChange(data);
                      }
                    }}
                  />
                )}
              />
            </Grid>
            {watchPlanYear?.length === 1 && watchRateBasis !== Constant.PLAN_EXPENSE_DATA_ONE_TIME_SPECIAL && (
              <>
                <Grid item>
                  <Controller
                    rules={{
                      required: Constant.ERROR_MSG_REQUIRED_FIELD,
                      validate: {
                        planStartDate: (value) => ValidationUtil.START_DATE(null, value, getValues('endDate'), true)
                      }
                    }}
                    defaultValue={null}
                    render={({ field, fieldState: { error } }) => (
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                          {...field}
                          maxDate={watchPlanEndDate ? moment.utc(watchPlanEndDate) : undefined}
                          inputFormat={Constant.FORMAT_DATE}
                          id="input-startDate"
                          label="Start Date"
                          fullWidth
                          value={(() => {
                            const raw = getValues('startDate');
                            return raw ? moment.utc(raw) : null;
                          })()}
                          onChange={(date) => {
                            resetDuplicateError();
                            if (date && moment.isMoment(date)) {
                              field.onChange(date.toISOString());
                            } else {
                              field.onChange(null);
                            }
                            trigger('startDate');
                          }}
                          onBlur={field.onBlur}
                          slotProps={{
                            textField: {
                              required: true,
                              error: !!error,
                              helperText: error?.message ?? null,
                              onBlur: field.onBlur
                            }
                          }}
                        />
                      </LocalizationProvider>
                    )}
                    name="startDate"
                    control={control}
                  />
                </Grid>
                <Grid item>
                  <Controller
                    rules={{
                      required: Constant.ERROR_MSG_REQUIRED_FIELD,
                      validate: {
                        planEndDate: (value) => ValidationUtil.END_DATE(null, getValues('startDate'), value, true)
                      }
                    }}
                    defaultValue={null}
                    render={({ field, fieldState: { error } }) => (
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                          {...field}
                          minDate={watchPlanStartDate ? moment.utc(watchPlanStartDate) : undefined}
                          inputFormat={Constant.FORMAT_DATE}
                          id="input-endDate"
                          label="End Date"
                          fullWidth
                          value={(() => {
                            const raw = getValues('endDate');
                            return raw ? moment.utc(raw) : null;
                          })()}
                          onChange={(date) => {
                            resetDuplicateError();
                            if (date && moment.isMoment(date)) {
                              field.onChange(date.toISOString());
                            } else {
                              field.onChange(null);
                            }
                            trigger('endDate');
                          }}
                          onBlur={field.onBlur}
                          slotProps={{
                            textField: {
                              required: true,
                              error: !!error,
                              helperText: error?.message ?? null,
                              onBlur: field.onBlur
                            }
                          }}
                        />
                      </LocalizationProvider>
                    )}
                    name="endDate"
                    control={control}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Grid>

        <Grid item xs={4}>
          <Grid container spacing={2} direction="column">
            <Grid item>
              <Controller
                name="expenseCategory"
                rules={{
                  required: Constant.ERROR_MSG_REQUIRED_FIELD
                }}
                control={control}
                defaultValue={''}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    {...field}
                    id="input-expenseCategory"
                    options={expenseCategories.map((ec) => ec.name)}
                    getOptionLabel={(option) => option}
                    renderOption={(props, option, { index }) => (
                      <li key={option + '-' + index} {...props}>
                        {option}
                      </li>
                    )}
                    renderInput={(params) => <TextField {...params} label=" Expense Category" error={!!error} helperText={error?.message ?? null} />}
                    onChange={(_, data) => {
                      resetDuplicateError();
                      field.onChange(data);
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item>
              <Controller
                name="expenseName"
                control={control}
                defaultValue={''}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    {...field}
                    id="input-expenseName"
                    options={expenseNames.map((en) => en.name)}
                    getOptionLabel={(option) => option}
                    isOptionEqualToValue={(option, value) => option === value}
                    renderOption={(props, option) => {
                      const { key, ...rest } = props;
                      return (
                        <li key={option} {...rest}>
                          {option}
                        </li>
                      );
                    }}
                    renderInput={(params) => <TextField {...params} label="Expense Name" error={!!error} helperText={error?.message ?? null} />}
                    onChange={(_, data) => {
                      resetDuplicateError();
                      field.onChange(data ? data : null);
                      trigger();
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item>
              <Controller
                name="rateBasis"
                rules={{
                  required: Constant.ERROR_MSG_REQUIRED_FIELD
                }}
                control={control}
                defaultValue={''}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Autocomplete
                      {...field}
                      id="input-rateBasis"
                      options={rateBasis.map((rb) => rb.name)}
                      getOptionLabel={(option) => option}
                      renderOption={(props, option) => (
                        <li key={option.id} {...props}>
                          {option}
                        </li>
                      )}
                      renderInput={(params) => <TextField {...params} label="Rate Basis" error={!!error} helperText={error?.message ?? null} />}
                      onChange={(_, data) => {
                        resetDuplicateError();
                        field.onChange(data);
                      }}
                    />
                    {watchRateBasis === Constant.PLAN_EXPENSE_DATA_ONE_TIME_SPECIAL && (
                      <Typography color="error">
                        *Attention: one-time special expense will result in applying the same amount to each of the plans selected. If the intention is to apply the expense only
                        once for the given period, then select only one plan.
                      </Typography>
                    )}
                  </>
                )}
              />
            </Grid>
            {watchRateBasis === Constant.PLAN_EXPENSE_RATE_BASIS_PER_CLAIM && (
              <>
                <Grid item>
                  <Controller
                    render={({ field, fieldState: { error } }) => (
                      <FormControlLabel {...field} control={<Checkbox color="primary" defaultChecked />} label="RX Covered" labelPlacement="end" />
                    )}
                    defaultValue={true}
                    control={control}
                    name="rxCovered"
                  />
                </Grid>
                <Grid item>
                  <Controller
                    render={({ field, fieldState: { error } }) => <FormControlLabel {...field} control={<Checkbox color="primary" />} label="Med Covered" labelPlacement="end" />}
                    defaultValue={false}
                    control={control}
                    name="medCovered"
                  />
                </Grid>
              </>
            )}
            {watchRateBasis === Constant.PLAN_EXPENSE_DATA_ONE_TIME_SPECIAL && watchPlanYear?.length === 1 ? (
              watchExpenseName &&
              watchExpenseName.toLowerCase() === Constant.PLAN_EXPENSE_EXPENSE_NAME_RX_REBATE && (
                <Grid item>
                  <Controller
                    render={({ field, fieldState: { error } }) => (
                      <FormControlLabel {...field} control={<Checkbox color="primary" />} label="Apply To All Plans" labelPlacement="end" />
                    )}
                    defaultValue={false}
                    control={control}
                    name="applicableToAllPlans"
                  />
                </Grid>
              )
            ) : (
              <Grid item>
                <Controller
                  render={({ field, fieldState: { error } }) => (
                    <FormControlLabel {...field} control={<Checkbox color="primary" />} label="Rate By Election Tier" labelPlacement="end" />
                  )}
                  defaultValue={false}
                  control={control}
                  name="isRateByTier"
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item lg={4}>
          <Grid container spacing={2} direction="column">
            {!(watchRateBasis === Constant.PLAN_EXPENSE_DATA_ONE_TIME_SPECIAL && watchPlanYear?.length === 1) && (
              <>
                {watchRateByTier ? (
                  Array.isArray(fields) &&
                  fields.map((et, index) => (
                    <Grid item key={et.id || index}>
                      <Controller
                        rules={{
                          validate: (value) => {
                            if (
                              [
                                Constant.PLAN_EXPENSE_EXPENSE_NAME_RX_REBATE,
                                Constant.PLAN_EXPENSE_EXPENSE_NAME_RX_REBATE_1,
                                Constant.PLAN_EXPENSE_EXPENSE_NAME_RX_REBATE_2
                              ].includes((watchExpenseName || '').toLowerCase())
                            ) {
                              if (value > 0) {
                                return 'Rate must be negative value';
                              }
                            }
                            if (!+value) {
                              return 'Rate must be number';
                            }

                            if (getValues('planExpenseRates').filter((er) => er.rate).length === 0) {
                              return 'Atleast one rate against election tier is required.';
                            }
                          }
                        }}
                        render={({ field, fieldState: { error } }) => (
                          <TextField id={`input-et-${index}`} fullWidth type="number" {...field} error={!!error} helperText={error?.message ?? null} label={et.name} />
                        )}
                        name={`planExpenseRates[${index}].rate`}
                        control={control}
                      />
                    </Grid>
                  ))
                ) : (
                  <Grid item>
                    <Controller
                      rules={{
                        required: Constant.ERROR_MSG_REQUIRED_FIELD,
                        validate: (value) => {
                          if (
                            [Constant.PLAN_EXPENSE_EXPENSE_NAME_RX_REBATE, Constant.PLAN_EXPENSE_EXPENSE_NAME_RX_REBATE_1, Constant.PLAN_EXPENSE_EXPENSE_NAME_RX_REBATE_2].includes(
                              (watchExpenseName || '').toLowerCase()
                            )
                          ) {
                            if (value > 0) {
                              return 'Rate must be negative value';
                            }
                          }
                        }
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <TextField id="input-rate" fullWidth type="number" {...field} error={!!error} helperText={error?.message ?? null} label="Rate" />
                      )}
                      name="rate"
                      control={control}
                    />
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </Grid>

        <Grid item xs={12}>
          {watchRateBasis === Constant.PLAN_EXPENSE_DATA_ONE_TIME_SPECIAL && watchPlanYear?.length === 1 && (
            <Grid container spacing={1}>
              <Grid item lg={12}>
                <Typography>Special Expense Rates</Typography>
              </Grid>
              <Grid item lg={12}>
                <Divider />
              </Grid>
              <Grid item lg={12}>
                <SpecialExpenseForm control={control} planYears={watchPlanYear} planStartDate={watchPlanStartDate} planEndDate={watchPlanEndDate} />
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </>
  );
};
export default React.memo(PlanExpenseDetail);
